import { useState } from "react";
import { Dialog } from "primereact/dialog";
import BPService from "../../../services/resource/bpService";
import { formatarMoeda } from "../../../utils/solicitacoes/formatters";
import { classeSaldo } from "../../../utils/solicitacoes/orcamento";
import SaldoProjetadoBpService from "../../../services/resource/saldoProjetadoBpService";
import { SaldoProjetadoResumo, rotuloMesReferencia } from "./SaldoProjetadoBpBotao";

const bpService = new BPService();
const saldoProjetadoService = new SaldoProjetadoBpService();

const ORDEM_MESES = [
    "JANEIRO", "FEVEREIRO", "MARCO", "ABRIL", "MAIO", "JUNHO",
    "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO",
];

const MESES_LABEL = {
    JANEIRO: "Jan", FEVEREIRO: "Fev", MARCO: "Mar", ABRIL: "Abr",
    MAIO: "Mai", JUNHO: "Jun", JULHO: "Jul", AGOSTO: "Ago",
    SETEMBRO: "Set", OUTUBRO: "Out", NOVEMBRO: "Nov", DEZEMBRO: "Dez",
};

function ordenarMeses(mapa = {}) {
    return Object.keys(mapa).sort((a, b) => ORDEM_MESES.indexOf(a) - ORDEM_MESES.indexOf(b));
}

/**
 * Tabela somente-leitura de rateio orçamentário — usada no detalhe de Compras e de Lançamento de
 * Despesa. Consome a lista já enriquecida com saldo (RateioOrcamentarioItemDTO). "Saldo Disponível"
 * é o saldo atual da conta BP antes de subtrair este rateio, para o usuário ver quanto ainda existe
 * de orçamento antes de distribuir os valores. O botão de cada conta BP abre o modal "Conta BP", que
 * além do orçado x realizado mensal mostra o saldo projetado (orçado menos o que já foi lançado no
 * MyDash) e as solicitações do mês de referência.
 */
export default function RateioOrcamentarioTabela({ itens = [] }) {
    const [bpSelecionado, setBpSelecionado] = useState(null);
    const [saldoProjetado, setSaldoProjetado] = useState(null);
    const [carregandoBpId, setCarregandoBpId] = useState(null);

    if (itens.length === 0) return null;

    // Contas a Pagar (rateio de receita/despesa): linhas sem Conta BP, então sem responsável/saldo/BP.
    const receitaDespesa = itens.every(item => !item.baseBpId && item.contaFinanceiraCodigo);

    // Agrupa visualmente as linhas da mesma nota fiscal, sem precisar de rowspan.
    const itensOrdenados = [...itens].sort((a, b) => (a.numeroNotaFiscal ?? "").localeCompare(b.numeroNotaFiscal ?? ""));

    function verBpCompleto(baseBpId) {
        if (!baseBpId) return;
        setCarregandoBpId(baseBpId);
        // O saldo projetado é complementar: se falhar, o modal abre só com o orçado x realizado.
        const saldo = saldoProjetadoService.consultar(baseBpId)
            .then(res => res.data)
            .catch(() => null);
        Promise.all([bpService.buscarPorId(baseBpId), saldo])
            .then(([resBp, dadosSaldo]) => {
                setSaldoProjetado(dadosSaldo);
                setBpSelecionado(resBp.data);
            })
            .catch(() => {})
            .finally(() => setCarregandoBpId(null));
    }

    function fecharBp() {
        setBpSelecionado(null);
        setSaldoProjetado(null);
    }

    return (
        <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-muted">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2 font-semibold">Nota Fiscal</th>
                        <th className="px-3 py-2 font-semibold">Filial</th>
                        <th className="px-3 py-2 font-semibold">Centro de Custo</th>
                        <th className="px-3 py-2 font-semibold">{receitaDespesa ? "Conta Financeira" : "Conta"}</th>
                        {!receitaDespesa && <th className="px-3 py-2 font-semibold">Responsavel</th>}
                        {!receitaDespesa && <th className="px-3 py-2 font-semibold text-right">Saldo (Mês)</th>}
                        <th className="px-3 py-2 font-semibold text-right">Valor do Rateio</th>
                        {!receitaDespesa && <th className="px-3 py-2 font-semibold text-center">BP</th>}
                    </tr>
                </thead>
                <tbody>
                    {itensOrdenados.map((item, index) => (
                        <tr key={`${item.baseBpId ?? index}-${item.numeroNotaFiscal ?? ""}-${index}`} className="border-t border-border text-[11px]">
                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.numeroNotaFiscal}</td>
                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.filialNome}</td>
                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.centroCustoCodigo} · {item.centroCustoDescricao}</td>
                            <td className="px-3 py-2 text-foreground whitespace-nowrap">
                                {receitaDespesa
                                    ? `${item.contaFinanceiraCodigo} · ${item.contaFinanceiraNome ?? ""}`
                                    : `${item.contaReduzido} · ${item.contaContabilDescricao}`}
                            </td>
                            {!receitaDespesa && <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.responsavelNome}</td>}
                            {!receitaDespesa && (
                                <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${classeSaldo(item.saldoAtual, item.orcamentoMes)}`}>
                                    {formatarMoeda(item.saldoAtual)}
                                </td>
                            )}
                            <td className="px-3 py-2 text-right text-foreground whitespace-nowrap">{formatarMoeda(item.valorRateado ?? item.valor)}</td>
                            {!receitaDespesa && <td className="px-3 py-2 text-center whitespace-nowrap">
                                <button
                                    type="button"
                                    title="Ver conta BP completa e saldo projetado"
                                    disabled={!item.baseBpId}
                                    onClick={() => verBpCompleto(item.baseBpId)}
                                    className="text-primary hover:bg-primary/10 rounded-full p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <i className={`fa ${carregandoBpId === item.baseBpId ? "fa-spinner fa-spin" : "fa-eye"}`} />
                                </button>
                            </td>}
                        </tr>
                    ))}
                </tbody>
            </table>

            <Dialog
                header={"Conta BP - " + bpSelecionado?.ano}
                visible={!!bpSelecionado}
                onHide={fecharBp}
                blockScroll
                className="w-[90%]"
            >
                {bpSelecionado && (
                    <div className="flex flex-col gap-4 p-1  pt-2">
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 text-sm rounded-lg bg-muted px-4 py-3">
                             <div>
                                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Filial</span>
                                <span className="block text-foreground font-medium">{bpSelecionado.filialNome}</span>
                            </div>
                            <div className="col-span-2">
                                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Conta</span>
                                <span className="block text-foreground font-medium">{bpSelecionado.contaReduzido} · {bpSelecionado.contaDescricao}</span>
                            </div>
                                                    <div>
                                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Centro de Custo</span>
                                <span className="block text-foreground font-medium">{bpSelecionado.centroCustoCodigo} · {bpSelecionado.centroCustoDescricao}</span>
                            </div>
                             <div>
                                <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Responsável</span>
                                <span className="block text-foreground font-medium">{bpSelecionado.ResponsavelNome || "—"}</span>
                            </div>
                        </div>

                        {bpSelecionado.valoresMensais && Object.keys(bpSelecionado.valoresMensais).length > 0 && (() => {
                            const meses = ordenarMeses(bpSelecionado.valoresMensais);
                            const despesas = bpSelecionado.despesasMensaisAtual || {};
                            const totalOrcado = meses.reduce((soma, mes) => soma + (Number(bpSelecionado.valoresMensais[mes]) || 0), 0);
                            const totalRealizado = meses.reduce((soma, mes) => soma + (Number(despesas[mes]) || 0), 0);
                            const totalSaldo = totalOrcado - totalRealizado;
                            return (
                                <section className="flex flex-col gap-2">
                                <h3 className="text-sm font-semibold text-foreground">
                                    Visão oficial do BP <span className="font-normal text-muted-foreground">· orçado x realizado (Fortes)</span>
                                </h3>
                                <div className="border border-border rounded-lg overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead className="bg-muted">
                                            <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                                                <th className="px-2 py-2 font-semibold">Mês</th>
                                                {meses.map(mes => (
                                                    <th key={mes} className="px-2 py-2 font-semibold text-right">{MESES_LABEL[mes] || mes}</th>
                                                ))}
                                                <th className="px-2 py-2 font-semibold text-right border-l border-border">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-t border-border">
                                                <td className="px-2 py-2 font-semibold text-foreground whitespace-nowrap">Orçado</td>
                                                {meses.map(mes => (
                                                    <td key={mes} className="px-2 py-2 text-right text-foreground whitespace-nowrap">{formatarMoeda(bpSelecionado.valoresMensais[mes])}</td>
                                                ))}
                                                <td className="px-2 py-2 text-right font-semibold text-foreground whitespace-nowrap border-l border-border">{formatarMoeda(totalOrcado)}</td>
                                            </tr>
                                            <tr className="border-t border-border">
                                                <td className="px-2 py-2 font-semibold text-foreground whitespace-nowrap">Realizado</td>
                                                {meses.map(mes => (
                                                    <td key={mes} className="px-2 py-2 text-right text-foreground whitespace-nowrap">{formatarMoeda(despesas[mes])}</td>
                                                ))}
                                                <td className="px-2 py-2 text-right font-semibold text-foreground whitespace-nowrap border-l border-border">{formatarMoeda(totalRealizado)}</td>
                                            </tr>
                                            <tr className="border-t border-border">
                                                <td className="px-2 py-2 font-semibold text-foreground whitespace-nowrap">Saldo</td>
                                                {meses.map(mes => {
                                                    const orcado = Number(bpSelecionado.valoresMensais[mes]) || 0;
                                                    const realizado = Number(despesas[mes]) || 0;
                                                    const saldo = orcado - realizado;
                                                    return (
                                                        <td key={mes} className={`px-2 py-2 text-right font-semibold whitespace-nowrap ${classeSaldo(saldo, orcado)}`}>
                                                            {formatarMoeda(saldo)}
                                                        </td>
                                                    );
                                                })}
                                                <td className={`px-2 py-2 text-right font-semibold whitespace-nowrap border-l border-border ${classeSaldo(totalSaldo, totalOrcado)}`}>
                                                    {formatarMoeda(totalSaldo)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                </section>
                            );
                        })()}

                        {saldoProjetado && (
                            <section className="flex flex-col gap-2 ">
                                <h3 className="text-sm font-semibold text-foreground">
                                    Visão MyDash <span className="font-normal text-muted-foreground">· solicitações lançadas em {rotuloMesReferencia(saldoProjetado)}</span>
                                </h3>
                                <SaldoProjetadoResumo dados={saldoProjetado} />
                            </section>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
}
